import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChefHat, MoreHorizontal, Recycle, UtensilsCrossed } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { fetchRetailCatalog, fetchRetailInventory } from "@/lib/api/retail";
import type { InventoryItemKind } from "@/features/retail/types/datasets";
import KitchenPrepDialog from "@/features/inventory/components/KitchenPrepDialog";
import KitchenWasteDialog from "@/features/inventory/components/KitchenWasteDialog";
import KitchenTransferDialog from "@/features/inventory/components/KitchenTransferDialog";
import {
  buildKitchenRows,
  type KitchenInventoryRow,
} from "@/features/inventory/utils/kitchenRows";

const kindBadgeTone: Record<InventoryItemKind, string> = {
  retail_product:
    "border border-slate-300/80 bg-slate-100/80 text-slate-700 dark:border-slate-700/70 dark:bg-slate-800/70 dark:text-slate-200",
  ingredient:
    "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200",
  prep_item:
    "border border-amber-200/70 bg-amber-100/60 text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200",
};

const statusTone: Record<KitchenInventoryRow["status"], string> = {
  healthy:
    "border border-emerald-200/70 bg-emerald-100/60 text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/15 dark:text-emerald-200",
  low: "border border-amber-200/70 bg-amber-100/60 text-amber-700 dark:border-amber-400/35 dark:bg-amber-500/15 dark:text-amber-200",
  out: "border border-rose-200/70 bg-rose-100/60 text-rose-700 dark:border-rose-500/35 dark:bg-rose-500/15 dark:text-rose-200",
};

const KitchenInventoryPage = () => {
  const { t } = useTranslation(["common", "retail"]);
  const { formatNumber } = useLocaleFormatter();

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
  });

  const inventoryQuery = useQuery({
    queryKey: ["retail", "inventory"],
    queryFn: ({ signal }) => fetchRetailInventory({ signal }),
  });

  const isLoading = catalogQuery.isLoading || inventoryQuery.isLoading;
  const isError = catalogQuery.isError || inventoryQuery.isError;

  const rows = useMemo(
    () =>
      buildKitchenRows(inventoryQuery.data ?? [], catalogQuery.data ?? []).sort((left, right) =>
        left.status === right.status ? right.available - left.available : left.status.localeCompare(right.status),
      ),
    [catalogQuery.data, inventoryQuery.data],
  );

  const kindCounts = useMemo(() => {
    let ingredient = 0;
    let prep = 0;
    let attention = 0;
    for (const row of rows) {
      if (row.classification.kind === "ingredient") {
        ingredient += 1;
      }
      if (row.classification.kind === "prep_item") {
        prep += 1;
      }
      if (row.status !== "healthy") {
        attention += 1;
      }
    }
    return { ingredient, prep, attention };
  }, [rows]);

  const [prepLevel, setPrepLevel] = useState<KitchenInventoryRow | null>(null);
  const [wasteLevel, setWasteLevel] = useState<KitchenInventoryRow | null>(null);
  const [transferLevel, setTransferLevel] = useState<KitchenInventoryRow | null>(null);

  const renderQuantity = (row: KitchenInventoryRow, value: number) => {
    const unit = row.classification.displayUnit?.abbreviation ?? row.classification.unit?.abbreviation ?? "ea";
    return `${formatNumber(value)} ${unit}`;
  };

  const renderThreshold = (row: KitchenInventoryRow) => {
    if (row.reorderPoint === null) {
      return t("retail.inventory.table.notAvailable", { defaultValue: "-" });
    }
    return renderQuantity(row, row.reorderPoint);
  };

  const renderStatus = (row: KitchenInventoryRow) => {
    if (row.status === "low") {
      return t("retail.inventory.table.status.low", { defaultValue: "Low" });
    }
    if (row.status === "out") {
      return t("retail.inventory.table.status.out", { defaultValue: "Out" });
    }
    return t("retail.inventory.table.status.healthy", { defaultValue: "Healthy" });
  };

  const kindLabel = (kind: InventoryItemKind) => {
    if (kind === "ingredient") {
      return t("retail.inventory.kitchen.kind.ingredient", { defaultValue: "Ingredient" });
    }
    if (kind === "prep_item") {
      return t("retail.inventory.kitchen.kind.prep", { defaultValue: "Prep batch" });
    }
    return t("retail.inventory.kitchen.kind.retail", { defaultValue: "Retail" });
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <Badge className="border border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:border-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-200">
            {t("retail.inventory.kitchen.previewBadge", { defaultValue: "Kitchen preview" })}
          </Badge>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            {t("retail.inventory.kitchen.title", { defaultValue: "Kitchen inventory" })}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t("retail.inventory.kitchen.subtitle", {
              defaultValue: "Track prep batches, ingredient thresholds, and log production events.",
            })}
          </p>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-3">
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.summary.ingredients.label", { defaultValue: "Active ingredients" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-slate-900 dark:text-slate-100">
              {formatNumber(kindCounts.ingredient)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.summary.ingredients.description", {
                defaultValue: "Tracked for prep and production.",
              })}
            </p>
          </CardHeader>
        </Card>
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-300">
              {t("retail.inventory.kitchen.summary.prep.label", { defaultValue: "Prep batches" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-amber-600 dark:text-amber-300">
              {formatNumber(kindCounts.prep)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.summary.prep.description", {
                defaultValue: "Ready for service or staging.",
              })}
            </p>
          </CardHeader>
        </Card>
        <Card className="border border-slate-200/70 bg-white/90 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/50">
          <CardHeader className="space-y-1">
            <CardDescription className="text-xs uppercase tracking-wide text-rose-600 dark:text-rose-300">
              {t("retail.inventory.kitchen.summary.attention.label", { defaultValue: "Needs attention" })}
            </CardDescription>
            <CardTitle className="flex items-baseline gap-2 text-3xl font-bold text-rose-600 dark:text-rose-300">
              {formatNumber(kindCounts.attention)}
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.summary.attention.description", {
                defaultValue: "At or below par. Audit before next service.",
              })}
            </p>
          </CardHeader>
        </Card>
      </section>

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>
            {t("retail.inventory.errors.title", { defaultValue: "Unable to load inventory" })}
          </AlertTitle>
          <AlertDescription>
            {t("retail.inventory.errors.description", {
              defaultValue: "The storefront data feed is unavailable. Try again shortly.",
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="border border-slate-200/70 bg-white/92 shadow-sm backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/55">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              {t("retail.inventory.kitchen.table.title", { defaultValue: "Kitchen positions" })}
            </CardTitle>
            <CardDescription className="text-slate-500 dark:text-slate-400">
              {t("retail.inventory.kitchen.table.subtitle", {
                defaultValue: "Batch prep, log waste, and balance locations across the kitchen.",
              })}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("retail.inventory.kitchen.table.columns.item", { defaultValue: "Item" })}</TableHead>
                    <TableHead>{t("retail.inventory.kitchen.table.columns.kind", { defaultValue: "Type" })}</TableHead>
                    <TableHead className="text-right">
                      {t("retail.inventory.kitchen.table.columns.onHand", { defaultValue: "On hand" })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("retail.inventory.kitchen.table.columns.par", { defaultValue: "Par" })}
                    </TableHead>
                    <TableHead>
                      {t("retail.inventory.kitchen.table.columns.status", { defaultValue: "Status" })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("retail.inventory.kitchen.table.columns.actions", { defaultValue: "Actions" })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.level.id}
                      className="border-slate-200/70 transition-colors hover:bg-emerald-50/40 dark:border-slate-800/70 dark:hover:bg-emerald-500/10"
                    >
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-slate-900 dark:text-slate-100">
                            {row.product?.name ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
                          </span>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            {row.product?.sku ? <span>{row.product.sku}</span> : null}
                            {row.product?.sku ? <span>•</span> : null}
                            <span>{row.level.locationName || t("retail.receiving.unknownLocation", { defaultValue: "Unassigned location" })}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={kindBadgeTone[row.classification.kind]}>
                          {kindLabel(row.classification.kind)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900 dark:text-slate-100">
                        {renderQuantity(row, row.available)}
                      </TableCell>
                      <TableCell className="text-right text-slate-600 dark:text-slate-300">
                        {renderThreshold(row)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusTone[row.status]}>{renderStatus(row)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                              <MoreHorizontal className="h-4 w-4" />
                              {t("retail.inventory.kitchen.table.actions.label", { defaultValue: "Actions" })}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem
                              onSelect={() => setPrepLevel(row)}
                              className="gap-2"
                            >
                              <UtensilsCrossed className="h-4 w-4 text-emerald-500" />
                              {t("retail.inventory.kitchen.table.actions.logPrep", { defaultValue: "Log prep batch" })}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => setWasteLevel(row)}
                              className="gap-2"
                            >
                              <Recycle className="h-4 w-4 text-amber-500" />
                              {t("retail.inventory.kitchen.table.actions.logWaste", { defaultValue: "Log waste" })}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => setTransferLevel(row)}
                              className="gap-2"
                            >
                              <ChefHat className="h-4 w-4 text-slate-500" />
                              {t("retail.inventory.kitchen.table.actions.transfer", { defaultValue: "Transfer" })}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-sm text-slate-500 dark:text-slate-400"
                      >
                        {t("retail.inventory.kitchen.table.empty", {
                          defaultValue: "No kitchen inventory positions yet.",
                        })}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <KitchenPrepDialog
        open={Boolean(prepLevel)}
        level={prepLevel?.level ?? null}
        classification={prepLevel?.classification ?? null}
        productName={prepLevel?.product?.name ?? null}
        locationName={prepLevel?.level.locationName ?? null}
        onOpenChange={(open) => {
          if (!open) {
            setPrepLevel(null);
          }
        }}
      />

      <KitchenWasteDialog
        open={Boolean(wasteLevel)}
        level={wasteLevel?.level ?? null}
        classification={wasteLevel?.classification ?? null}
        productName={wasteLevel?.product?.name ?? null}
        locationName={wasteLevel?.level.locationName ?? null}
        onOpenChange={(open) => {
          if (!open) {
            setWasteLevel(null);
          }
        }}
      />

      <KitchenTransferDialog
        open={Boolean(transferLevel)}
        sourceLevel={transferLevel?.level ?? null}
        classification={transferLevel?.classification ?? null}
        productName={transferLevel?.product?.name ?? null}
        locationName={transferLevel?.level.locationName ?? null}
        levels={rows.map((row) => row.level)}
        onOpenChange={(open) => {
          if (!open) {
            setTransferLevel(null);
          }
        }}
      />
    </div>
  );
};

export default KitchenInventoryPage;
